/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { SpreadTypeId } from '@/types';

export interface ReadingDraft {
  spreadTypeId: SpreadTypeId | null;
  question: string;
  photo: File | null;
}

interface ReadingDraftContextValue {
  draft: ReadingDraft | null;
  setDraft: (draft: ReadingDraft) => void;
  clearDraft: () => void;
}

const ReadingDraftContext = createContext<ReadingDraftContextValue | null>(
  null,
);

/**
 * Holds the in-progress reading form (spread type, question, photo) in
 * memory, above the routed page tree, so an unrelated redirect (e.g. the
 * session dying mid-composition) doesn't unmount `SpreadForm` and silently
 * discard what the user typed/attached/picked. Mode is deliberately not part
 * of the draft — it always follows `initialMode` from the URL, so picking a
 * different reading type from Home never gets silently overridden by a
 * leftover selection (a restored `spreadTypeId` that isn't valid for the
 * resulting mode is simply ignored by `SpreadForm`'s existing fallback
 * logic). Intentionally not persisted to storage: the photo is a live
 * `File`, and the redirect this protects against is a client-side route
 * change, not a page reload.
 */
export function ReadingDraftProvider({ children }: { children: ReactNode }) {
  const [draft, setDraftState] = useState<ReadingDraft | null>(null);

  const setDraft = useCallback((next: ReadingDraft) => {
    setDraftState(next);
  }, []);

  const clearDraft = useCallback(() => {
    setDraftState(null);
  }, []);

  const value = useMemo<ReadingDraftContextValue>(
    () => ({ draft, setDraft, clearDraft }),
    [draft, setDraft, clearDraft],
  );

  return (
    <ReadingDraftContext.Provider value={value}>
      {children}
    </ReadingDraftContext.Provider>
  );
}

export function useReadingDraft(): ReadingDraftContextValue {
  const ctx = useContext(ReadingDraftContext);
  if (!ctx) {
    throw new Error(
      'useReadingDraft must be used within ReadingDraftProvider',
    );
  }
  return ctx;
}
